import React, { useCallback, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import block from 'bem-cn'
import { Field, Form, Formik } from 'formik'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_PATHS } from 'router/routes'

import { ReactComponent as CloseSVG } from 'shared/img/close.svg'
import logo from 'shared/img/logo.webp'

import { transLang } from 'shared/locale'
import { localeAuthKeysType } from 'shared/locale/en-US'

import { signIn, signUp } from 'features/auth/actions'
import { selectAuthCommunication } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { selectCurrenciesList } from 'features/settings/selectors'
import { selectLanguage } from 'features/userSettings/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import Button from 'components/UIKit/Button/Button'
import { Input } from 'components/UIKit/Input/Input'

import { phoneMaskUtils, registrationFields } from './data'
import { RegistrationSchema } from './data/validate'
import { usePhoneMask } from './hooks/usePhoneMask'

import './SignUp.scss'

interface ISignUpProps {
  path: string
}

const b = block('sign-up')

const SignUp: React.FC<ISignUpProps> = ({ path }) => {
  const dispatch = useDispatch()

  const modal = useModalContext()

  const locale = useSelector(selectLocaleDict, shallowEqual).auth
  const { isLoading } = useSelector(selectAuthCommunication, shallowEqual).signUp
  const lang = useSelector(selectLanguage, shallowEqual)
  const currencies = useSelector(selectCurrenciesList, shallowEqual)
  const { closeModal } = useModalContext()

  const handleCloseClick = useCallback(() => modal.closeModal(MODAL_ROUTE_URLS.SIGN_UP())(), [modal])

  const closeSignUpModal = useCallback(() => closeModal(path)(), [closeModal, path])

  const {
    formattedNumber: phoneNumber,
    clearNumber,
    handleChange: setPhoneNumber,
    handleFormateChange,
  } = usePhoneMask(phoneMaskUtils)

  const onSubmitHandler = useCallback(
    (values: typeof registrationFields, callback: () => void) => {
      const userData = {
        info: {
          mobile_number: values.digitPhone,
        },
        username: values.username,
        password1: values.password,
        password2: values.repeatPassword,
        language: transLang[lang],
      }

      dispatch(signUp({ ...userData }, { callback }))
    },
    [dispatch, lang],
  )

  const countryBlock = useMemo(
    () => (
      <div className={b('country-block')}>
        <div>{phoneMaskUtils.code}</div>
      </div>
    ),
    [],
  )

  return (
    <div className={b()}>
      <div className={b('heading')}>
        <span>{locale.signUp}</span>
        <CloseSVG className={b('close')} onClick={handleCloseClick} />
      </div>
      <Formik
        // eslint-disable-next-line max-len
        key={`${currencies.length}`} // нужен чтобы работал ререндер когда приходит список валют + работал persist
        initialValues={{
          ...registrationFields,
          digitPhone: clearNumber,
          phone: phoneNumber,
        }}
        validationSchema={RegistrationSchema}
        onSubmit={values => onSubmitHandler(values, closeSignUpModal)}>
        {({ values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <div className={b('inputs')}>
              <div className={b('input-wrapper')}>
                <label className={b('label')}>{locale.username}</label>
                <div className={b('input')}>
                  <Input
                    type="text"
                    name="username"
                    placeholder={`${locale.username}`}
                    value={values.username}
                    isRequired
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.username && touched.username && locale[errors.username as localeAuthKeysType]}
                  />
                </div>
              </div>

              <div className={b('input-wrapper')}>
                <label className={b('label')}>{locale.password}</label>
                <div className={b('input')}>
                  <Input
                    name="password"
                    type="password"
                    placeholder={`${locale.password}`}
                    value={values.password}
                    isRequired
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.password && touched.password && locale[errors.password as localeAuthKeysType]}
                  />
                </div>
              </div>
              <div className={b('input-wrapper')}>
                <label className={b('label')}>{locale.repeatPassword}</label>
                <div className={b('input')}>
                  <Input
                    name="repeatPassword"
                    type="password"
                    placeholder={`${locale.repeatPassword}`}
                    value={values.repeatPassword}
                    isRequired
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      errors.repeatPassword &&
                      touched.repeatPassword &&
                      locale[errors.repeatPassword as localeAuthKeysType]
                    }
                  />
                </div>
              </div>
              <div className={b('input-wrapper')}>
                <label className={b('label')}>{locale.mobilePhone}</label>
                <Field name="digitPhone" type="hidden" value={values.digitPhone} />
                <div className={b('input')}>
                  <Input
                    name="phone"
                    type="tel"
                    adornmentType="country-code"
                    value={values.phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('phone', handleFormateChange(e.currentTarget.value))
                      setFieldValue('digitPhone', setPhoneNumber(e.currentTarget.value))
                    }}
                    onBlur={handleBlur}
                    startAdornment={countryBlock}
                    rounded={false}
                    isRequired
                    error={touched.phone && errors.digitPhone && locale[errors.digitPhone as localeAuthKeysType]}
                  />
                </div>
              </div>
            </div>
            <div className={b('btn-wrapper')}>
              <Button type="submit" text={locale.signUp} disabled={isLoading} loading={isLoading} />
            </div>
            <div className={b('divider')} />
            <div className={b('logo-wrapper')}>
              <img src={logo} alt="logo" className={b('logo')} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default SignUp
